import { useQuery } from "@rocicorp/zero/react";
import { useZero } from "@/hooks/use-zero";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Trash2 } from "lucide-react";

import ClosableCard from "@/components/closable-card";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLogin } from "@/hooks/use-login";
import { DocumentQueryRepository } from "@/zero/document.repo";

export default function DocumentList() {
  const z = useZero();
  const { user } = useLogin();
  const navigate = useNavigate();

  const docRepo = new DocumentQueryRepository(z);

  const [documents] = useQuery(docRepo.getDocumentIndex(user?.id ?? "anon"));
  const [documentName, setDocumentName] = useState("");

  const handleCreateDocument = () => {
    const trimmedDocumentName = documentName.trim();
    if (trimmedDocumentName && user?.id) {
      docRepo.createDocument({
        name: trimmedDocumentName,
        owner: user.id,
        content: [],
        createdOn: Date.now(),
      });
    }
    setDocumentName("");
  };

  const handleOpenDocument = async (documentId: string) => {
    // Navigate to document view
    navigate(`/documents/${documentId}`);
  };

  const handleDeleteDocument = (documentId: string, documentName: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${documentName}"? This action cannot be undone.`,
      )
    ) {
      docRepo.deleteDocument(documentId);
    }
  };

  return (
    <ClosableCard className="relative" title="Documents">
      <CardContent className="space-y-4">
        {documents.length === 0 && <p>No documents found</p>}
        {documents.map((document) => (
          <div
            key={document.id}
            className="flex items-center justify-between p-2 space-x-8 border rounded"
          >
            <div>
              <h3 className="font-medium">{document.name}</h3>
              <p className="text-sm text-muted-foreground">
                Created: {new Date(document.createdOn).toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenDocument(document.id)}
              >
                Open
              </Button>
              {user?.id === document.owner && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleDeleteDocument(document.id, document.name)
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
        {user?.id && (
          <div className="space-y-2">
            <Label htmlFor="document-name">Document Name</Label>
            <Input
              id="document-name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Enter document name"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleCreateDocument();
                }
              }}
            />
            <Button
              onClick={handleCreateDocument}
              className="w-full"
              disabled={!documentName.trim()}
            >
              Create New Document
            </Button>
          </div>
        )}
        {!user?.id && (
          <Button onClick={() => navigate("/login")}>
            Sign in to create documents
          </Button>
        )}
      </CardContent>
    </ClosableCard>
  );
}
